import {
  averageDay,
  dayTotals,
  hasMacros,
  kcalOf,
  MEAL_SLOT_LABEL,
  mealTotals,
  type PlanBody,
} from "@/modules/meal_plans/surfaces";
import {
  AverageDayCard,
  DayMacroHeader,
  ItemMacros,
  MacroLine,
} from "./MacroSummary";
import type { PlanPhase } from "@/modules/meal_plans/surfaces";
import type { PlanListItemView } from "./mealPlanViews";
import { StatusBadge, type Tone } from "./ui";

/**
 * A meal plan, read (H53). What the Consumer sees, and what the clinic sees in
 * the preview of a plan it can no longer change.
 *
 * **Printable by construction** rather than by a separate PDF (H53 part 4):
 * plain document structure, no controls inside it, and `break-inside-avoid` on
 * each day so a printed day does not split across a page. The screens hide
 * their chrome with `print:hidden`.
 */

/** The badge each phase wears. One mapping, both sides of the plan. */
export const PHASE_TONE: Record<PlanPhase, Tone> = {
  draft: "warning",
  upcoming: "info",
  current: "success",
  past: "muted",
  archived: "muted",
};

export function PlanPhaseBadge({
  phase,
  label,
}: {
  phase: PlanPhase;
  label: string;
}) {
  return <StatusBadge tone={PHASE_TONE[phase]}>{label}</StatusBadge>;
}

/**
 * The plan's body. When it carries macros (H54) the reader also gets the
 * average day above the days, each day's calories and split in its header, a
 * total under each meal, and each item's own figures. A plan whose clinic
 * switched the macros off arrives with none, so none of that is drawn: the
 * gate is in the read, and this component only reflects it.
 */
export function MealPlanReader({ body }: { body: PlanBody }) {
  if (body.days.length === 0 && body.guidance === "") {
    return (
      <p className="rounded-xl border border-line bg-surface p-5 text-sm text-body shadow-sm">
        Nothing in this plan yet.
      </p>
    );
  }

  const showMacros = hasMacros(body);

  return (
    <div className="space-y-5">
      {showMacros && <AverageDayCard totals={averageDay(body)} />}

      {body.guidance && (
        <section className="rounded-xl border border-line bg-surface p-5 shadow-sm print:border-0 print:p-0 print:shadow-none">
          <h2 className="mb-1 font-bold text-ink">Guidance</h2>
          <p className="whitespace-pre-line text-sm text-body">{body.guidance}</p>
        </section>
      )}

      {body.days.map((day, index) => (
        <section
          key={`${index}-${day.label}`}
          className="break-inside-avoid rounded-xl border border-line bg-surface p-5 shadow-sm print:border-0 print:p-0 print:shadow-none"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <h2 className="font-bold text-ink">{day.label}</h2>
            {showMacros && <DayMacroHeader totals={dayTotals(day)} />}
          </div>
          <dl className="space-y-4">
            {day.meals.map((meal, mealIndex) => (
              <div key={`${mealIndex}-${meal.slot}`}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {MEAL_SLOT_LABEL[meal.slot]}
                </dt>
                <dd>
                  <ul className="mt-1 space-y-1.5 text-sm text-body">
                    {meal.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-ink">{item.text}</span>
                        {item.quantity && (
                          <span className="text-muted">{item.quantity}</span>
                        )}
                        {item.macros && (
                          <ItemMacros macros={item.macros} kcalValue={kcalOf(item.macros)} />
                        )}
                      </li>
                    ))}
                  </ul>
                  {showMacros && meal.items.length > 1 && (
                    <MacroLine
                      totals={mealTotals(meal)}
                      className="mt-2 border-t border-line pt-1.5"
                    />
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}

/**
 * A list of plans, for the Customer File and for the Consumer's own list.
 *
 * Each row is a link, so the whole row is the target; the badge and the dates
 * are what somebody scans for.
 */
export type PlanListLink = (props: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) => React.ReactNode;

export function MealPlanList({
  plans,
  hrefFor,
  Link,
  empty,
}: {
  plans: PlanListItemView[];
  hrefFor: (planId: string) => string;
  Link: PlanListLink;
  empty: string;
}) {
  if (plans.length === 0) {
    return (
      <p className="rounded-xl border border-line bg-surface p-5 text-sm text-body shadow-sm">
        {empty}
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {plans.map((plan) => (
        <li key={plan.id}>
          <Link
            href={hrefFor(plan.id)}
            className="block rounded-xl border border-line bg-surface p-4 shadow-sm transition-colors hover:border-accent"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-ink">{plan.title}</span>
              <PlanPhaseBadge phase={plan.phase} label={plan.phaseLabel} />
            </div>
            <p className="mt-1 text-sm text-muted">
              {plan.dates}
              {plan.businessName ? ` · ${plan.businessName}` : ` · ${plan.authorName}`}
            </p>
            {plan.visit && <p className="mt-0.5 text-xs text-muted">{plan.visit}</p>}
          </Link>
        </li>
      ))}
    </ul>
  );
}
